import { RULE_IDS, type Diagnostic, type Rule, type ValidationReport } from '@dpem/ontology';
import * as z from 'zod';

import { DescriptionSchema, IdentifierSchema, ShortTextSchema } from '../common/primitives.js';

/**
 * authoring context — graph validation. The rule catalogue and the diagnostic
 * shape belong to `@dpem/ontology`; this module is only their transport form,
 * pinned to the ontology's types by the assertions at the bottom of the file.
 */

export const RuleIdSchema = z.enum(RULE_IDS);
export type RuleId = z.infer<typeof RuleIdSchema>;

export const SeveritySchema = z.enum(['error', 'warning']);
export type Severity = z.infer<typeof SeveritySchema>;

export const DiagnosticSchema = z.strictObject({
  ruleId: RuleIdSchema,
  severity: SeveritySchema,
  message: z.string().min(1).max(2_000),
  nodeId: z.string().min(1).max(200).optional(),
  property: z.string().min(1).max(200).optional(),
  /** Human-readable remedy; the authoring validation table links it to the element. */
  fix: DescriptionSchema.optional(),
});
export type DiagnosticDto = z.infer<typeof DiagnosticSchema>;

export const RuleSchema = z.strictObject({
  id: RuleIdSchema,
  title: ShortTextSchema,
  description: DescriptionSchema,
  severity: SeveritySchema,
});
export type RuleDto = z.infer<typeof RuleSchema>;

export const ValidationReportSchema = z.strictObject({
  diagnostics: z.array(DiagnosticSchema),
  errorCount: z.int().min(0),
  warningCount: z.int().min(0),
  valid: z.boolean(),
});
export type ValidationReportDto = z.infer<typeof ValidationReportSchema>;

export const RunValidationRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  /** Rules an author has consciously waived for this run. */
  disabledRules: z.array(RuleIdSchema).max(RULE_IDS.length).optional(),
  /** Closed-world check: ids that MTH-005 may resolve a `variantOf` target against. */
  knownMethodIds: z.array(z.string().min(1).max(200)).max(500).optional(),
});
export type RunValidationRequest = z.infer<typeof RunValidationRequestSchema>;

export const RunValidationResponseSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  report: ValidationReportSchema,
  /** The catalogue travels with the report so the UI can render rule metadata offline. */
  rules: z.array(RuleSchema),
  /** True when nothing blocks a publish; warnings alone do not block. */
  publishable: z.boolean(),
});
export type RunValidationResponse = z.infer<typeof RunValidationResponseSchema>;

export const ListRulesRequestSchema = z.strictObject({
  severity: SeveritySchema.optional(),
});
export type ListRulesRequest = z.infer<typeof ListRulesRequestSchema>;
export const ListRulesResponseSchema = z.strictObject({ rules: z.array(RuleSchema) });
export type ListRulesResponse = z.infer<typeof ListRulesResponseSchema>;

/** Compile-time proof the DTOs still mirror the ontology types they transport. */
type Extends<TSub extends TSuper, TSuper> = TSub extends TSuper ? true : never;
export type DiagnosticDtoMirrorsOntology = Extends<Diagnostic, DiagnosticDto>;
export type RuleDtoMirrorsOntology = Extends<Rule, RuleDto>;
export type ValidationReportDtoMirrorsOntology = Extends<
  Omit<ValidationReport, 'diagnostics'> & { diagnostics: readonly DiagnosticDto[] },
  Omit<ValidationReportDto, 'diagnostics'> & { diagnostics: readonly DiagnosticDto[] }
>;
